/**
 * usePropertiesFetch - Properties Fetching with Retry Logic Hook
 *
 * Purpose:
 * Manages properties fetching from API with portal-aware endpoint selection, retry logic for
 * auth and transient errors, and skip logic for unauthenticated paths. Extracted from
 * SessionContext.jsx to reduce responsibility overload.
 *
 * High-level Workflow:
 * 1. On pathname change or retry trigger: Check if fetch should be skipped (unauthenticated paths)
 * 2. Fetch properties from API (portal-aware: /api/v1/owner/properties or /api/v1/admin/properties)
 * 3. Normalize API response format (handle admin vs owner API differences)
 * 4. Handle errors with retry logic:
 *    - Auth errors (401/403): Retry up to 3 times with delays [500, 1000, 2000]ms
 *    - Transient errors (502/503/504/network): Retry up to 5 times with delays [250, 500, 1000, 2000, 4000]ms
 * 5. Update properties state, loading state, and error state
 *
 * Assumptions & Invariants:
 * - Portal detection is hostname-based (admin.* or owner/clcvacation domains)
 * - Properties are pre-filtered by backend (owner receives only accessible properties)
 * - Skip fetch on unauthenticated paths (/login, /owner/reset-password, /forgot-password)
 * - Retry counts are bounded (max 3 auth retries, max 5 transient retries)
 * - AbortController implemented in Phase 4 for request cancellation
 *
 * Dependencies:
 * Internal:
 *   - services/ownerApi.js: getOwnerProperties() for owner portal properties fetch
 *   - services/adminPropertiesService.js: getAdminProperties() for admin portal properties fetch
 *   - utils/portalDetection.js: detectPortal(), checkPortalApiGuard()
 *   - utils/sessionContextUtils.js: shouldSkipPropertiesFetch()
 * External:
 *   - react: useState, useEffect, useCallback, useRef
 *
 * Version: 1.1.0
 *
 * Changelog:
 * - 2026-01-26: Phase 3 - Extracted from SessionContext.jsx (verbatim, no logic changes)
 * - 2026-01-26: Phase 4 - Added AbortController for request cancellation, mount guards, and log gating
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getProperties as getOwnerProperties } from '../services/ownerApi.js';
import { getProperties as getAdminProperties } from '../services/adminPropertiesService.js';
import { detectPortal, checkPortalApiGuard } from '../utils/portalDetection.js';
import { shouldSkipPropertiesFetch } from '../utils/sessionContextUtils.js';

/**
 * usePropertiesFetch - Properties fetching with retry logic
 * 
 * @param {string} pathname - Current pathname (for skip logic and fetch trigger)
 * @returns {Object} Properties state and retry function
 */
export function usePropertiesFetch(pathname) {
  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  const [properties, setProperties] = useState([]); // Live properties from API
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [propertiesError, setPropertiesError] = useState(null);

  // AbortController ref to track in-flight requests and cancel on unmount or new fetch
  const abortControllerRef = useRef(null);
  // Mounted ref to prevent setState after unmount
  const isMountedRef = useRef(true);

  // Fetch trigger for manual retry (e.g. "Couldn't load property" -> Retry)
  const [propertiesFetchTrigger, setPropertiesFetchTrigger] = useState(0);
  const retryProperties = useCallback(() => {
    setPropertiesError(null);
    setPropertiesLoading(true);
    setPropertiesFetchTrigger(prev => prev + 1);
  }, []);

  // Helper to check if we should set state (component still mounted)
  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  // Helper to check if debug logging is enabled
  const shouldLog = () => {
    if (typeof window === 'undefined') return false;
    return process.env.NODE_ENV === 'development' ||
           new URLSearchParams(window.location.search).get('debug') === '1';
  };

  /**
   * Properties Fetch Effect - Portal-aware properties fetching with retry logic
   * 
   * Intent: Fetches properties from API (portal-aware endpoint selection), normalizes response format,
   * and handles retries for auth errors (401/403) and transient errors (502/503/504/network).
   * Skips fetch on unauthenticated paths to avoid unnecessary API calls.
   * 
   * Retry Strategy:
   * - Auth errors (401/403): Retry up to 3 times with delays [500, 1000, 2000]ms
   * - Transient errors (502/503/504/network): Retry up to 5 times with delays [250, 500, 1000, 2000, 4000]ms
   * - After max retries: Auth errors → set empty properties, Transient errors → set error state
   * 
   * Reliability (Phase 4):
   * - AbortController cancels in-flight requests on unmount or new fetch
   * - Mount guards prevent setState after unmount
   * - AbortError handled silently (not user-visible)
   * - Loading state guaranteed to resolve (never stuck)
   * 
   * Dependencies: [pathname, propertiesFetchTrigger, debug] - Re-runs on navigation or manual retry
   */
  // Cleanup on unmount: abort any in-flight requests and mark as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Fetch properties from API. Re-runs when pathname changes (e.g. /login -> /calendar) or retry triggered.
  useEffect(() => {
    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this fetch
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    let authRetryCount = 0;
    let transientRetryCount = 0;
    const AUTH_MAX_RETRIES = 3;
    const AUTH_RETRY_DELAYS = [500, 1000, 2000];
    const TRANSIENT_MAX_RETRIES = 5;
    const TRANSIENT_RETRY_DELAYS = [250, 500, 1000, 2000, 4000];

    const fetchProperties = async (isRetry = false, isTransientRetry = false) => {
      // Check if aborted before proceeding
      if (signal.aborted) return;

      if (shouldSkipPropertiesFetch(pathname)) {
        // Clear the previous account's catalog before a later sign-in.
        safeSetState(setProperties, []);
        safeSetState(setPropertiesError, null);
        safeSetState(setPropertiesLoading, false);
        return;
      }

      if (!isRetry && !isTransientRetry) {
        // Keep the authorized catalog visible while owner routes revalidate it.
        // The API still checks access for every data request. Blocking on each
        // route change restarts Calendar's data effects and duplicates requests.
        if (detectPortal() !== 'owner' || properties.length === 0) {
          safeSetState(setPropertiesLoading, true);
        }
        safeSetState(setPropertiesError, null);
      }
      
      const isAnyRetry = isRetry || isTransientRetry;
      if (typeof window !== 'undefined' && window.performance && !isAnyRetry) {
        performance.mark('owner_properties_fetch_start');
      }

      const perfDebug = typeof window !== 'undefined' && (
        import.meta.env.VITE_OWNER_PERF_DEBUG === '1' ||
        new URLSearchParams(window.location.search).get('perf_debug') === '1'
      );
      if (perfDebug && !isAnyRetry && shouldLog()) {
        console.log('[OWNER_PERF] Properties fetch started');
      }
      
      try {
        // Portal-aware: use admin service on admin domain, owner service on owner domain
        const portal = detectPortal();
        const getPropertiesFn = portal === 'admin' ? getAdminProperties : getOwnerProperties;
        const expectedUrl = portal === 'admin' ? '/api/v1/admin/properties' : '/api/v1/owner/properties';
        
        // Guardrail: warn if wrong portal detected
        checkPortalApiGuard(expectedUrl);
        
        // Pass signal to API call for cancellation
        const response = portal === 'admin' 
          ? await getPropertiesFn({ signal })
          : await getPropertiesFn(signal);
        
        // Check if aborted after await
        if (signal.aborted) return;
        
        // API DEBUG: Log properties response (gated by ?api_debug=1)
        const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const apiDebug = urlParams?.get('api_debug') === '1';
        
        if (apiDebug && shouldLog()) {
          console.log('[SessionContext] API DEBUG - Properties response:', {
            url: expectedUrl,
            portal,
            status: 200,
            outcome: response.outcome,
            hasItems: !!response.items,
            hasDataItems: !!response.data?.items,
            isArray: Array.isArray(response),
            itemsCount: response.items?.length || response.data?.items?.length || (Array.isArray(response) ? response.length : 0),
            firstItem: response.items?.[0] || response.data?.items?.[0] || (Array.isArray(response) ? response[0] : null),
          });
        }
        
        // Normalize API response to match expected format
        // Admin API returns array directly or { items: [...] }
        // Owner API returns { outcome, message, items: [...] } or { data: { items: [...] } }
        let items;
        if (portal === 'admin') {
          // Admin API: can return array directly or { items: [...] }
          items = Array.isArray(response) ? response : (response.items || response.data?.items || []);
        } else {
          // Owner API: { outcome, items: [...] } or { data: { items: [...] } }
          items = response.items || response.data?.items || [];
        }
        const normalizedProperties = items
          .filter(item => {
            // Filter out items without valid listing_id
            const listingId = item.listing_id || item.id;
            const numId = typeof listingId === 'string' ? parseInt(listingId, 10) : listingId;
            return !isNaN(numId) && numId > 0;
          })
          .map(item => {
            const listingId = item.listing_id || item.id;
            const numListingId = typeof listingId === 'string' ? parseInt(listingId, 10) : listingId;
            return {
              // Keep API fields intact (don't overwrite id)
              ...item,
              // Ensure listing_id is numeric (canonical identifier)
              listing_id: numListingId,
              // Add ui_key for React keys (separate from API id)
              ui_key: String(numListingId),
              // Normalize name field
              name: item.name || item.property_name || 'Unknown',
              property_name: item.property_name || item.name || 'Unknown',
            };
          });
        
        if (debug && shouldLog()) {
          console.log('[OWNER_SELECT] [SessionContext] Properties loaded/normalized:', {
            count: normalizedProperties.length,
            first5ListingIds: normalizedProperties.slice(0, 5).map(p => p.listing_id),
            first5Names: normalizedProperties.slice(0, 5).map(p => p.name || p.property_name),
            authRetryCount,
            transientRetryCount,
          });
        }
        
        if (typeof window !== 'undefined' && window.performance && !isAnyRetry) {
          performance.mark('owner_properties_fetch_end');
          performance.measure('properties_fetch_ms', 'owner_properties_fetch_start', 'owner_properties_fetch_end');
        }
        if (perfDebug && !isAnyRetry && shouldLog()) {
          const measure = performance.getEntriesByName('properties_fetch_ms')[0];
          console.log('[OWNER_PERF] Properties fetch completed', {
            count: normalizedProperties.length,
            durationMs: measure?.duration?.toFixed(2),
            authRetryCount,
            transientRetryCount,
          });
        }
        
        // Check if aborted before setting state
        if (signal.aborted) return;
        
        // Preserve property object identity when revalidation returns the same
        // catalog. Calendar effects depend on selectedProperty; replacing an
        // identical object starts availability and snapshot requests again.
        safeSetState(setProperties, previous =>
          JSON.stringify(previous) === JSON.stringify(normalizedProperties)
            ? previous
            : normalizedProperties
        );
        safeSetState(setPropertiesLoading, false);
        safeSetState(setPropertiesError, null);
      } catch (error) {
        // Handle AbortError silently (not a user-visible error)
        if (error.name === 'AbortError' || signal.aborted) {
          // Ensure loading state is cleared even on abort
          safeSetState(setPropertiesLoading, false);
          return;
        }

        const status = error?.status ?? 'err';
        const isAuthError = status === 401 || status === 403 ||
          error?.errorCode === 'AUTH_REQUIRED' ||
          error?.errorCode === 'AUTH_INVALID_SESSION' ||
          error?.message?.includes('authentication') ||
          error?.message?.includes('Authentication required') ||
          error?.message?.includes('Admin access requires authentication');

        const isTransientError = status === 502 || status === 503 || status === 504 ||
          status === 0 ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError';

        if (isTransientError && transientRetryCount < TRANSIENT_MAX_RETRIES) {
          transientRetryCount++;
          const delay = TRANSIENT_RETRY_DELAYS[transientRetryCount - 1];
          if ((debug || perfDebug) && shouldLog()) {
            console.log(`[SessionContext] Properties fetch transient error (retry ${transientRetryCount}/${TRANSIENT_MAX_RETRIES} in ${delay}ms):`, status, error?.message || String(error));
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          if (!signal.aborted) return fetchProperties(false, true);
        }

        if (isAuthError && authRetryCount < AUTH_MAX_RETRIES) {
          authRetryCount++;
          const delay = AUTH_RETRY_DELAYS[authRetryCount - 1];
          if ((debug || perfDebug) && shouldLog()) {
            console.log(`[SessionContext] Properties fetch auth error (retry ${authRetryCount}/${AUTH_MAX_RETRIES} in ${delay}ms):`, error?.message || String(error));
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          if (!signal.aborted) return fetchProperties(true, false);
        }

        // Check if aborted before setting error state
        if (signal.aborted) {
          safeSetState(setPropertiesLoading, false);
          return;
        }

        if (isAuthError) {
          if (debug && shouldLog()) {
            console.log('[SessionContext] Properties fetch failed after max auth retries - not authenticated');
          }
          safeSetState(setProperties, []);
          safeSetState(setPropertiesLoading, false);
          safeSetState(setPropertiesError, null);
          return;
        }

        if (isTransientError) {
          if (shouldLog()) {
            console.error('[SessionContext] Properties fetch max transient retries exhausted:', status, error?.message || String(error));
          }
          safeSetState(setPropertiesError, error);
          safeSetState(setPropertiesLoading, false);
          return;
        }

        if (shouldLog()) {
          console.error('[SessionContext] Error fetching properties:', error);
        }
        safeSetState(setPropertiesError, error);
        safeSetState(setPropertiesLoading, false);
      }
    };

    fetchProperties();

    return () => {
      // Abort in-flight request on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [pathname, propertiesFetchTrigger, debug]);

  return {
    properties,
    propertiesLoading,
    propertiesError,
    retryProperties,
  };
}
